// График «Воронка по дням»: линии visits и clicks за выбранное окно.
// Окно переключается между 7/30/90 днями. Timezone берётся из
// браузера пользователя — клик в 23:55 МСК должен попадать в тот же
// день, что и в дашборде у владельца.

import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDailyFunnel } from '../../lib/hooks.ts';

type WindowDays = 7 | 30 | 90;

interface Props {
  theoryId: string;
}

function isoDate(d: Date): string {
  // YYYY-MM-DD в UTC — даты передаются без времени, но для генерации
  // окна важна детерминированность (24-часовые шаги от сегодняшней
  // полуночи UTC).
  return d.toISOString().slice(0, 10);
}

function computeWindow(days: WindowDays): { from: string; to: string } {
  const todayUtc = new Date();
  todayUtc.setUTCHours(0, 0, 0, 0);
  const to = isoDate(todayUtc);
  const fromDate = new Date(todayUtc.getTime() - (days - 1) * 24 * 3600 * 1000);
  return { from: isoDate(fromDate), to };
}

function formatXAxisDate(date: string): string {
  // 2026-05-12 → 12.05
  const [, m, d] = date.split('-');
  return `${d}.${m}`;
}

export function DailyFunnelChart({ theoryId }: Props): JSX.Element {
  const [days, setDays] = useState<WindowDays>(30);
  const window = useMemo(() => computeWindow(days), [days]);
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);

  const query = useDailyFunnel(theoryId, { from: window.from, to: window.to, tz });

  const chartData = useMemo(
    () =>
      (query.data?.days ?? []).map((d) => ({
        date: d.date,
        label: formatXAxisDate(d.date),
        visits: d.visits,
        clicks: d.clicks,
        impressions: d.impressions,
      })),
    [query.data],
  );

  const hasImpressions = chartData.some(
    (d) => typeof d.impressions === 'number' && d.impressions > 0,
  );

  const isEmpty =
    !query.isLoading &&
    chartData.length > 0 &&
    chartData.every((d) => d.visits === 0 && d.clicks === 0) &&
    !hasImpressions;

  return (
    <div className="card">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-xs text-fg-muted">Часовой пояс: {tz}</div>
        <div className="inline-flex overflow-hidden rounded border border-border text-xs">
          {([7, 30, 90] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1 transition-colors ${
                days === d ? 'bg-accent text-white' : 'bg-bg-elevated text-fg-muted hover:text-fg'
              }`}
            >
              {d} дней
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <div className="h-64 animate-pulse rounded bg-bg-elevated" />
      ) : query.isError ? (
        <div className="py-8 text-center text-fg-muted">Не удалось загрузить статистику.</div>
      ) : isEmpty ? (
        <div className="py-8 text-center text-fg-muted">Событий за выбранный период нет.</div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.1} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'currentColor' }}
                tickMargin={6}
                minTickGap={20}
              />
              <YAxis
                yAxisId="left"
                allowDecimals={false}
                tick={{ fontSize: 11, fill: 'currentColor' }}
                width={32}
              />
              {hasImpressions ? (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: 'currentColor' }}
                  width={48}
                />
              ) : null}
              <Tooltip
                contentStyle={{
                  background: 'var(--tw-bg-elevated, #1a1a1a)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.date ?? ''}
              />
              {hasImpressions ? (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="impressions"
                  name="Показы"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ) : null}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="visits"
                name="Визиты"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="clicks"
                name="Клики"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-fg-muted">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#3b82f6' }} />
          Визиты
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#10b981' }} />
          Клики
        </div>
        {hasImpressions ? (
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#f59e0b' }} />
            Показы (правая ось)
          </div>
        ) : null}
      </div>
    </div>
  );
}
